import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataverseEntityInfo } from '@shared/api-types';
import { fetchDataverseEntities } from '@/lib/api';
import { ReusableFilterBuilder } from '../shared/ReusableFilterBuilder';
import { FilterBuilderContext } from '../shared/filter-builder-types';
import { EntityMappingsFieldProvider } from '../shared/field-providers';
import { DEFAULT_OPERATORS } from '../shared/operators-config';
import { FlexibleFilter } from '../flexible/flexible-filter-types';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface EntityMappingsFilterBuilderProps {
  onFiltersChange: (filters: FlexibleFilter[]) => void;
  initialFilters?: FlexibleFilter[];
  onValidationChange?: (isValid: boolean) => void;
  onRecordCountChange?: (count: number | null, entityName?: string) => void;
  onNormalizedFiltersChange?: (filters: import('@shared/api-types').NormalizedFilterSet) => void;
  className?: string;
  disabled?: boolean;
  // Entity context props
  parentEntity?: string;
  childEntity?: string;
  _parentLookupField?: string;
  _childLookupField?: string;
  // Context entities for auto-population (when cross-entity filter should use mapping entities)
  contextParentEntity?: string;
  contextChildEntity?: string;
  // Force specific filter type
  forceFilterType?: 'cross-entity' | 'static';
  // Association config for skipExisting support
  associationConfig?: {
    mode: string;
    skipExisting?: boolean;
    associationMethod?: 'lookup_update' | 'collection_add';
    navigationProperty?: string;
    lookupFieldName?: string;
    toEntity?: string;
  };
  // Modified date filter for count operations
  modifiedDateFilter?: {
    enabled: boolean;
    field: string;
    mode: 'absolute' | 'relative' | 'scheduler';
    absoluteRange?: { from?: string; to?: string };
    relativeRange?: { value: number; unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' };
  };
  // Record count props
  recordCount?: number | null;
  recordCountEntity?: string | null;
  countingRecords?: boolean;
  onCountRecords?: () => void;
}

export const EntityMappingsFilterBuilder: React.FC<EntityMappingsFilterBuilderProps> = ({
  onFiltersChange,
  initialFilters,
  onValidationChange,
  onRecordCountChange,
  onNormalizedFiltersChange,
  className,
  disabled,
  parentEntity,
  childEntity,
  _parentLookupField,
  _childLookupField,
  contextParentEntity,
  contextChildEntity,
  forceFilterType,
  associationConfig,
  modifiedDateFilter,
  recordCount,
  recordCountEntity,
  countingRecords = false,
  onCountRecords
}) => {
  React.useEffect(() => {
    console.log('[EntityMappingsFilterBuilder] MOUNTED with props:', JSON.stringify({
      hasOnNormalizedFiltersChange: !!onNormalizedFiltersChange,
      initialFiltersCount: initialFilters?.length || 0,
      parentEntity,
      childEntity,
      initialFilters
    }, null, 2));
  }, []); // Only log once on mount

  // Use React Query to fetch entities
  const { data: entities, isLoading: entitiesLoading } = useQuery({
    queryKey: ["/api/entity-mappings/entities"],
    queryFn: () => fetchDataverseEntities(),
    enabled: true
  });

  // Split initial filters into source and target based on entity
  const { sourceFilters, targetFilters } = useMemo(() => {
    console.log('[EntityMappingsFilterBuilder] Splitting initialFilters:', JSON.stringify({
      initialFiltersCount: initialFilters?.length || 0,
      parentEntity,
      childEntity,
      initialFilters
    }, null, 2));

    if (!initialFilters || initialFilters.length === 0) {
      console.log('[EntityMappingsFilterBuilder] No initial filters, returning empty arrays');
      return { sourceFilters: [], targetFilters: [] };
    }

    const source: FlexibleFilter[] = [];
    const target: FlexibleFilter[] = [];

    initialFilters.forEach((filter, idx) => {
      if (filter.type === 'static') {
        const staticCond = filter.condition as any;
        console.log(`[EntityMappingsFilterBuilder] Processing filter ${idx}:`, JSON.stringify({
          entity: staticCond.entity,
          targetEntity: staticCond.targetEntity,
          field: staticCond.field,
          operator: staticCond.operator,
          parentEntity,
          childEntity
        }, null, 2));

        // Determine if this is a source or target filter based on entity
        if (staticCond.entity === parentEntity || staticCond.targetEntity === 'parent') {
          console.log(`[EntityMappingsFilterBuilder] Filter ${idx} → SOURCE`);
          source.push(filter);
        } else if (staticCond.entity === childEntity || staticCond.targetEntity === 'child') {
          console.log(`[EntityMappingsFilterBuilder] Filter ${idx} → TARGET`);
          target.push(filter);
        } else {
          // Default to source for backward compatibility
          console.log(`[EntityMappingsFilterBuilder] Filter ${idx} → SOURCE (default)`);
          source.push(filter);
        }
      } else {
        // Cross-entity filters go to target
        console.log(`[EntityMappingsFilterBuilder] Cross-entity filter ${idx} → TARGET`);
        target.push(filter);
      }
    });

    console.log('[EntityMappingsFilterBuilder] Split result:', JSON.stringify({
      sourceCount: source.length,
      targetCount: target.length
    }, null, 2));

    return { sourceFilters: source, targetFilters: target };
  }, [initialFilters, parentEntity, childEntity]);

  // Combine source and target filters for parent callback
  const handleFiltersChange = (sourceFilters: FlexibleFilter[], targetFilters: FlexibleFilter[]) => {
    const combined = [...sourceFilters, ...targetFilters];
    console.log('[EntityMappingsFilterBuilder] Filters changed:', JSON.stringify({
      source: sourceFilters.length,
      target: targetFilters.length,
      combined: combined.length,
      sourceFilters: sourceFilters,
      targetFilters: targetFilters
    }, null, 2));
    onFiltersChange(combined);

    // If parent wants normalized filters, build them properly from the split sections
    if (onNormalizedFiltersChange) {
      // Build normalized filters CORRECTLY - source filters go to source array, target filters go to target array
      const normalized: import('@shared/api-types').NormalizedFilterSet = {
        source: sourceFilters.map(f => {
          if (f.type === 'static') {
            const cond = f.condition as any;
            console.log('[EntityMappingsFilterBuilder] Building SOURCE normalized filter from:', JSON.stringify(cond, null, 2));
            return {
              id: cond.id,
              field: cond.field,
              operator: cond.operator,
              value: cond.value,
              stage: 'retrieval' as const
            };
          }
          return null;
        }).filter(Boolean) as any[],
        target: targetFilters.map(f => {
          if (f.type === 'static') {
            const cond = f.condition as any;
            console.log('[EntityMappingsFilterBuilder] Building TARGET normalized filter from:', JSON.stringify(cond, null, 2));
            return {
              id: cond.id,
              field: cond.field,
              operator: cond.operator,
              value: cond.value,
              stage: cond.operator === 'eq_source_field' || cond.operator === 'ne_source_field' ? 'processing' : 'retrieval'
            };
          }
          return null;
        }).filter(Boolean) as any[],
        metadata: {}
      };

      console.log('[EntityMappingsFilterBuilder] Built normalized filters:', JSON.stringify(normalized, null, 2));
      onNormalizedFiltersChange(normalized);
    }
  };

  // State for source and target filters
  const [localSourceFilters, setLocalSourceFilters] = React.useState<FlexibleFilter[]>(sourceFilters);
  const [localTargetFilters, setLocalTargetFilters] = React.useState<FlexibleFilter[]>(targetFilters);

  // Sync with initialFilters when they change
  React.useEffect(() => {
    setLocalSourceFilters(sourceFilters);
    setLocalTargetFilters(targetFilters);
  }, [sourceFilters, targetFilters]);

  // Create context for entity mappings
  const context = useMemo((): FilterBuilderContext => ({
    contextType: 'entity-mappings',
    entityProvider: async (): Promise<DataverseEntityInfo[]> => {
      // Return entities from the query
      return entities || [];
    },
    fieldProvider: new EntityMappingsFieldProvider(),
    availableOperators: DEFAULT_OPERATORS,
    enableCrossEntity: true,
    enableExport: true,
    maxFilters: 10,
    // Pass context entities for auto-population
    contextParentEntity,
    contextChildEntity
  }), [entities, contextParentEntity, contextChildEntity]);

  // Show loading state while entities are being fetched
  if (entitiesLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
        <span>Loading entities...</span>
      </div>
    );
  }

  // Generate contextual description based on sync mode
  const getSyncModeDescription = () => {
    if (parentEntity && childEntity) {
      return {
        source: `Applied when retrieving records from ${parentEntity} (Stage 1 - Retrieval)`,
        target: `Applied when processing ${childEntity} records (Stage 2 - Processing)`
      };
    }
    return {
      source: 'Applied at Stage 1 (Retrieval)',
      target: 'Applied at Stage 2 (Processing)'
    };
  };

  const descriptions = getSyncModeDescription();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Info Alert explaining the two-stage pipeline */}
      {parentEntity && childEntity && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Two-Stage Filter Pipeline:</strong> Source filters run during data retrieval (Stage 1),
            while Target filters run during processing (Stage 2). Cross-entity operators like "equals (source field)"
            only work in Target filters where both entities are available.
          </AlertDescription>
        </Alert>
      )}

      {/* Source Filters Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-sm">Stage 1</span>
              Source Filters
              {parentEntity && <span className="text-sm font-normal text-muted-foreground">({parentEntity})</span>}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{descriptions.source}</p>
          </div>

          <ReusableFilterBuilder
            context={context}
            initialFilters={localSourceFilters}
            onFiltersChange={(filters) => {
              setLocalSourceFilters(filters);
              handleFiltersChange(filters, localTargetFilters);
            }}
            onValidationChange={onValidationChange}
            disabled={disabled}
            title=""
            description=""
            forceFilterType={forceFilterType}
            filterLocation={parentEntity && childEntity ? "source" : undefined}
            parentEntity={parentEntity}
            childEntity={childEntity}
          />
        </CardContent>
      </Card>

      {/* Target Filters Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-sm">Stage 2</span>
              Target Filters
              {childEntity && <span className="text-sm font-normal text-muted-foreground">({childEntity})</span>}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{descriptions.target}</p>
            <div className="mt-2 text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-700">
              💡 <strong>Tip:</strong> Use cross-entity operators (like "equals (source field)") here to compare
              target fields with source fields
            </div>
          </div>

          <ReusableFilterBuilder
            context={context}
            initialFilters={localTargetFilters}
            onFiltersChange={(filters) => {
              setLocalTargetFilters(filters);
              handleFiltersChange(localSourceFilters, filters);
            }}
            onRecordCountChange={onRecordCountChange}
            disabled={disabled}
            title=""
            description=""
            forceFilterType={forceFilterType}
            associationConfig={associationConfig}
            modifiedDateFilter={modifiedDateFilter}
            _recordCount={recordCount}
            recordCountEntity={recordCountEntity}
            isCountingRecords={countingRecords}
            onCountRecords={onCountRecords}
            filterLocation={parentEntity && childEntity ? "target" : undefined}
            parentEntity={parentEntity}
            childEntity={childEntity}
          />
        </CardContent>
      </Card>
    </div>
  );
};
